import { useRef } from 'react';
import { audio } from '../constants/assets';
import { logInfo, logWarning } from '../utils/remoteLogger';

// Create a global element that will be re-used for audio playback
// throughout the runtime of the app.
const audioElement = new Audio();

let audioUnlocked = false;
let isPlayingOnlyNow = false;

type PlaybackOptions = {
  loop?: boolean;
  // The sound is only wanted right now, so a later user gesture must not play it.
  playOnlyNow?: boolean;
};

const UNLOCK_EVENTS = [
  'click',
  'touchstart',
  'pointerdown',
  'keydown',
] as const;

const unlock = () => {
  if (audioUnlocked) return;
  audioUnlocked = true;
  // if there is already a source, we can assume that the user is trying to play it and we should not override it with the silence file
  if (!audioElement.src || (isPlayingOnlyNow && audioElement.paused)) {
    // Tiny silent MP3 used to unlock HTMLAudioElement on gesture.
    // Playing a real source (even silent) is more reliably counted as a user-gesture
    // unlock than playing an empty Audio() element.
    audioElement.src = audio.SILENCE_250_MS;
  }
  audioElement
    .play()
    .then(() => {
      logInfo('useAudio', 'Audio unlocked successfully');
      if (audioElement.src === audio.SILENCE_250_MS) {
        audioElement.pause();
        audioElement.src = '';
      }
      UNLOCK_EVENTS.forEach((event) =>
        document.removeEventListener(event, unlock, { capture: true })
      );
    })
    .catch(() => {
      audioUnlocked = false;
      logInfo(
        `useAudio`,
        'Failed to unlock audio on user gesture, will retry on next gesture'
      );
    });
};

UNLOCK_EVENTS.forEach((event) =>
  document.addEventListener(event, unlock, { capture: true, passive: true })
);

const useAudio = () => {
  const startPlayback = (
    src: keyof typeof audio,
    { loop = false, playOnlyNow = false }: PlaybackOptions = {}
  ) => {
    isPlayingOnlyNow = playOnlyNow;
    audioElement.src = audio[src];
    audioElement.loop = loop;
    audioElement.play().catch((error) => {
      logWarning('useAudio', `Audio playback failed: ${src}`, error);
    });
  };

  const stopPlayback = () => {
    // There is no actual stop method.
    // When the playback is paused and there are no longer any references to it,
    // the memory will be freed by the garbage collector as usual.
    // As we will always keep a reference to the element, this does introduce a small memory overhead,
    // but as it will only be used to play short audio snippets, it should be negligible
    isPlayingOnlyNow = false;
    audioElement.pause();
    audioElement.src = '';
  };

  return useRef({
    startPlayback,
    stopPlayback,
  }).current;
};

export default useAudio;
